'use strict';

import _ from 'lodash';

import {
    SaveDataParameters,
    SaveDataResult
} from '~/shared/src/utilities/modules/data';
import {ItemsAndReferencesCollection} from '~/shared/src/utilities/dataUtils';

import {SAVE_DATA} from '~/frontend/src/constants';
import {
    StoreState,
    AppDispatch,
    Thunk
} from '~/frontend/src/store';
import xhr from '~/frontend/src//utilities/xhr';
import {setElusiveData} from '~/frontend/src/actions/elusiveData';
import {
    getModifiedData,
    getModifiedDataRecursive
} from '~/frontend/src/utilities/dataUtils';
import {setMessage} from '~/frontend/src/actions/intercom';


type Parameters = {
    [ key : string ] : any;
};


/**
 * Save data to backend
 *
 * @memberOf! Actions
 *
 * @param {string} dataContext The data context
 * @param {string} componentUid The uid of the calling component
 * @param {string} [requestUid=undefined] The uid of the request
 * @param {Schema} [schema='DATA']
 * @param {string} [command='data/save']
 * @param {boolean} [purgeDeleted=false]
 * @param {string[]} [additionalElusiveDataContexts=[]]
 * @param {number} [rootItemId]
 * @param {ItemsAndReferencesCollection} [forcedData]
 * @param {Parameters} [additionalRequestParams]
 *
 * @return {Promise<SaveDataResult>}
 */
const saveData = ( dataContext : string, componentUid : string, requestUid? : string, schema : Schema = 'DATA', command : string = 'data/save', purgeDeleted : boolean = false, additionalElusiveDataContexts : string[] = [], rootItemId? : number, forcedData? : ItemsAndReferencesCollection, additionalRequestParams? : Parameters ) : Thunk<Promise<SaveDataResult>> => async ( dispatch : AppDispatch, getState : () => StoreState ) : Promise<SaveDataResult> => {
    dispatch( {
        type: SAVE_DATA,
        dataContext,
        componentUid,
        requestUid,
        schema,
        command,
        purgeDeleted,
        additionalElusiveDataContexts,
        rootItemId,
        forcedData,
        additionalRequestParams
    } );

    let retVal : SaveDataResult = {
        success : false,
        data    : {
            itemMetaDataMapping             : {},
            propertyMetaDataMapping         : {},
            referenceMetaDataMapping        : {},
            referencePropertyMetaDataMapping: {}
        },
        metaData: {
            metrics: []
        }
    };

    const modifiedData = rootItemId ? getModifiedDataRecursive( dataContext, schema, rootItemId, false ) : getModifiedData( dataContext, schema );

    if ( forcedData ) {
        modifiedData.items = _.uniq( modifiedData.items.concat( forcedData.items ) );
        modifiedData.references = _.uniq( modifiedData.references.concat( forcedData.references ) );
    }

    const params : { schema : Schema } & SaveDataParameters & Parameters = {
              schema,
              data: {
                  items     : modifiedData.items.map( item => item.toJSON( 'modified' ) ),
                  references: modifiedData.references.map( reference => reference.toJSON( 'modified' ) )
              },
              ...additionalRequestParams
          },
          // remember ids to apply changes to elusive data
          itemIdsAddedOrModified : number[]                              = [],
          itemIdsDeleted : number[]                                      = [],
          referenceIdsAddedOrModified : number[]                         = [],
          referenceIdsDeleted : number[]                                 = [];

    // something to save
    if ( modifiedData.items.length || modifiedData.references.length || ( additionalRequestParams && Object.keys( additionalRequestParams ).length ) ) {
        const response = await xhr.post(
            `${BASE_URL}/api/${command}`,
            params,
            undefined,
            componentUid,
            requestUid,
            dispatch
        );

        if ( response?.data ) {
            retVal = response.data as SaveDataResult;

            const resultData = retVal.data;

            additionalElusiveDataContexts.unshift( dataContext );
            if ( dataContext !== componentUid ) {
                additionalElusiveDataContexts.push( componentUid );
            }

            if ( retVal.success ) {

                // commit items
                for ( const item of modifiedData.items.concat( Object.keys( resultData.itemMetaDataMapping ).map( itemId => global.data[ dataContext ].items[ itemId ] ).filter( mappedItem => !!mappedItem ) ) ) {

                    // purge deleted item
                    if ( purgeDeleted && item.isDeleted() ) {
                        itemIdsDeleted.push( item.id );

                        item.delete();
                    }
                    else {

                        // new item
                        if ( resultData.itemMetaDataMapping[ item.id ] ) {

                            itemIdsAddedOrModified.push( resultData.itemMetaDataMapping[ item.id ].id );

                            // item id changed? then delete old from elusive data
                            if ( item.id !== resultData.itemMetaDataMapping[ item.id ].id ) {
                                itemIdsDeleted.push( item.id );
                            }
                        }

                        // modified item
                        else {
                            itemIdsAddedOrModified.push( item.id );

                            // purge deleted properties
                            if ( purgeDeleted ) {
                                for ( const property of item.properties.entries() ) {
                                    if ( property[ 1 ].isDeleted() ) {
                                        item.properties.delete( property[ 0 ] );
                                    }
                                }
                                for ( const enumProperty of item.enumProperties.entries() ) {
                                    for ( const property of enumProperty[ 1 ].properties.values() ) {
                                        if ( property.isDeleted() ) {
                                            enumProperty[ 1 ].properties.delete( property.stringKey );
                                        }
                                    }

                                    if ( !enumProperty[ 1 ].properties.size ) {
                                        item.enumProperties.delete( enumProperty[ 0 ] );
                                    }
                                }
                            }
                        }

                        item.commit( resultData.itemMetaDataMapping, resultData.propertyMetaDataMapping ).clone();
                    }
                }

                // commit references
                for ( const reference of modifiedData.references.concat( Object.keys( resultData.referenceMetaDataMapping ).map( refId => global.data[ dataContext ].references[ refId ] ).filter( mappedReference => !!mappedReference ) ) ) {

                    // purge deleted reference
                    if ( purgeDeleted && reference.isDeleted() ) {
                        referenceIdsDeleted.push( reference.id );

                        reference.delete();
                    }
                    else {

                        // new reference
                        if ( resultData.referenceMetaDataMapping[ reference.id ] ) {
                            referenceIdsAddedOrModified.push( resultData.referenceMetaDataMapping[ reference.id ].id );

                            // reference id changed? then delete old from elusive data
                            if ( reference.id !== resultData.referenceMetaDataMapping[ reference.id ].id ) {
                                referenceIdsDeleted.push( reference.id );
                            }
                        }

                        // modified reference
                        else {
                            referenceIdsAddedOrModified.push( reference.id );

                            // purge deleted properties
                            if ( purgeDeleted ) {
                                for ( const property of reference.properties.entries() ) {
                                    if ( property[ 1 ].isDeleted() ) {
                                        reference.properties.delete( property[ 0 ] );
                                    }
                                }
                                for ( const enumProperty of reference.enumProperties.entries() ) {
                                    for ( const property of enumProperty[ 1 ].properties.values() ) {
                                        if ( property.isDeleted() ) {
                                            enumProperty[ 1 ].properties.delete( property.stringKey );
                                        }
                                    }

                                    if ( !enumProperty[ 1 ].properties.size ) {
                                        reference.enumProperties.delete( enumProperty[ 0 ] );
                                    }
                                }
                            }
                        }

                        reference.commit( resultData.referenceMetaDataMapping, resultData.referencePropertyMetaDataMapping, resultData.itemMetaDataMapping ).clone();
                    }
                }

                // update ElusiveData
                for ( const elusiveDataContext of additionalElusiveDataContexts ) {
                    const elusiveData    = getState().elusiveData?.[ elusiveDataContext ],
                          newElusiveData = {
                              ...elusiveData,
                              data: {
                                  ...elusiveData?.data
                              }
                          },
                          itemIds        = itemIdsAddedOrModified.concat( itemIdsDeleted ),
                          referenceIds   = referenceIdsAddedOrModified.concat( referenceIdsDeleted );

                    if ( itemIds.length ) {
                        newElusiveData.data.items = {
                            ...elusiveData?.data?.items
                        };

                        for ( const itemId of itemIds ) {
                            if ( itemIdsDeleted.includes( itemId ) ) {
                                delete newElusiveData.data.items[ itemId ];
                            }
                            else {
                                newElusiveData.data.items[ itemId ] = 1;
                            }
                        }
                    }

                    if ( referenceIds.length ) {
                        newElusiveData.data.references = {
                            ...elusiveData?.data?.references
                        };

                        for ( const referenceId of referenceIds ) {
                            if ( referenceIdsDeleted.includes( referenceId ) ) {
                                delete newElusiveData.data.references[ referenceId ];
                            }
                            else {
                                newElusiveData.data.references[ referenceId ] = 1;
                            }
                        }
                    }

                    dispatch( setElusiveData( elusiveDataContext, newElusiveData ) );

                    dispatch( setMessage(
                        elusiveDataContext,
                        'notification',
                        'saveSuccess',
                        {
                            itemMetaDataMapping             : resultData.itemMetaDataMapping,
                            propertyMetaDataMapping         : resultData.propertyMetaDataMapping,
                            referenceMetaDataMapping        : resultData.referenceMetaDataMapping,
                            referencePropertyMetaDataMapping: resultData.referencePropertyMetaDataMapping
                        }
                    ) );
                }
            }
            else {
                // update ElusiveData
                for ( const elusiveDataContext of additionalElusiveDataContexts ) {
                    dispatch( setMessage(
                        elusiveDataContext,
                        'notification',
                        'saveError',
                        resultData
                    ) );
                }
            }
        }
    }

    // nothing to save
    else {
        retVal.success = true;
    }

    return retVal;
}
export type SaveData = typeof saveData;

export default saveData;
